import classNames, { Argument } from "classnames/dedupe";
import React, { FC, ReactNode, useState, useCallback } from "react";
import noop from "../../../../helpers/noop";
import useUpdateEffect from "../../../../hooks/useUpdateEffect";
import LinkButton from "../../../ActionsSelectors/Button/LinkButton/LinkButton";
import { ILink } from "../../../ActionsSelectors/Link/Link";
import { TSelectorOption } from "../../../ActionsSelectors/Select/Select/Select.interface";
import ProductTitle from "../../../DataDisplay/ProductTitle/ProductTitle";
import { Typography } from "../../../Fundamentals/Typography/Typography";
import { IProductItem } from "../../../ProductCard/ProductCard.interface";
import { Image } from "../../../Utility/MediaBuilder/Image";
import { IImageData } from "../../../Utility/MediaBuilder/Image/Image.interface";
import { TThemeVariant } from "../../../Utility/ThemeProvider/ThemeContext";
import useTheme from "../../../Utility/ThemeProvider/useTheme";
import { TPrice } from "../../../DataDisplay";
import ProductConfig from "./ShoppingBagProductConfig";
import ShopingBagProductActions from "./ShoppingBagProductActions";
import ShopingBagQuantity from "./ShoppingBagQuantity";
import ShoppingBagAdditionInfo from "./ShoppingBagAdditionInfo";

export type TShoppingBagItemType = "default" | "mini" | "look";

export interface IShoppingBagItem extends Pick<IProductItem, "prices"> {
  classes?: {
    root: Argument;
  };
  id: string;
  type?: TShoppingBagItemType;
  variant?: TThemeVariant;
  opened?: boolean;
  labels?: {
    quantity?: ReactNode;
    selectQuantity?: ReactNode;
    colourText?: ReactNode;
    sizeText?: ReactNode;
    removeButton?: ReactNode;
    moveToFavouritesButton?: ReactNode;
    itemRemoved?: ReactNode;
    putBack?: ReactNode;
  };
  sportLoversPrice?: TPrice;
  brandName?: string;
  brandLink?: ILink;
  productName: string;
  productLink: ILink;
  customImage?: JSX.Element;
  productImage: IImageData;
  disabled?: boolean;
  readonly?: boolean;
  color?: string;
  size?: string;
  selectedQuantity: TSelectorOption["id"];
  availableQuantity: TSelectorOption[];
  isAddedToCart?: boolean;
  onQtyChange?(selectedQuantity: TSelectorOption["id"]): void;
  onPutBack?(): void;
  onRemove?(): void;
  onFavourite?(): void;
  handleAddToCart?(): void;
  isFavoriteAvailable?: boolean;
  hasDisableQuantity?: boolean;
  disableQty?: boolean;
  showSize?: boolean;
  hasSmallTitle?: boolean;
  isRemoveAvailable?: boolean;
}

// eslint-disable-next-line complexity
const ShoppingBagItem: FC<IShoppingBagItem> = ({
  classes,
  id,
  type = "default",
  variant,
  opened,
  labels = {
    quantity: "Qty",
    selectQuantity: "Select Quantity",
    colourText: "Colour",
    sizeText: "Size",
    removeButton: "Remove",
    moveToFavouritesButton: "Move to favorites",
    itemRemoved: "was removed",
    putBack: "Put back",
  },
  sportLoversPrice,
  brandName,
  brandLink,
  productName,
  productLink,
  customImage,
  productImage,
  disabled,
  color,
  size,
  prices,
  selectedQuantity,
  availableQuantity,
  readonly,
  isAddedToCart = false,
  onQtyChange,
  onRemove = noop,
  onFavourite = noop,
  onPutBack = noop,
  handleAddToCart = noop,
  isFavoriteAvailable = false,
  hasDisableQuantity = false,
  disableQty = false,
  showSize = true,
  hasSmallTitle = false,
  isRemoveAvailable = true,
}) => {
  const theme = useTheme(variant);
  const [selectedQty, setSelectedQty] =
    useState<TSelectorOption["id"]>(selectedQuantity);

  const [isItemRemoved, setIsItemRemoved] = useState<boolean>(false);

  useUpdateEffect(() => {
    onQtyChange?.(selectedQty);
  }, [selectedQty]);

  const handleQtyChange = useCallback(
    (newValue: TSelectorOption["id"]) => setSelectedQty(newValue),
    [],
  );

  const removeItem = useCallback(() => {
    onRemove();
    setIsItemRemoved(true);
  }, [onRemove]);

  const putBackItem = useCallback(() => {
    onPutBack();
    setIsItemRemoved(false);
  }, [onPutBack]);

  return isItemRemoved ? (
    <div className="shopping-bag--notification text-center mt-6 mb-6">
      <Typography
        variant="text-regular"
        as="span"
        data-testid="ShoppingBagItem__removed-txt"
      >
        {brandName} {productName} {labels.itemRemoved}
      </Typography>
      <LinkButton
        variant="bold"
        classes={{ root: "mt-6" }}
        type="button"
        as="button"
        data-testid="ShoppingBagItem__put-back"
        onClick={putBackItem}
      >
        {labels.putBack}
      </LinkButton>
    </div>
  ) : (
    <figure
      data-testid="ShoppingBagItem"
      key={id}
      className={classNames(
        "d-inline-flex product-card__shopping-bag",
        {
          "product-card__shopping-bag--look": type === "look",
          "product-card__shopping-bag--mini": type === "mini",
          "cursor-disabled": disabled,
        },
        classes?.root,
      )}
    >
      <div
        className={classNames("product-card__shopping-bag-image", {
          "product-card__shopping-bag-image--look": type === "look",
          "product-card__shopping-bag-image--mini": type === "mini",
          "product-card__shopping-bag-image--disabled": disabled,
        })}
      >
        {customImage ?? <Image {...productImage} />}
      </div>
      <figcaption
        className={classNames(
          {
            "flex-column justify-content-between pr-0": (
              ["mini", "look"] as TShoppingBagItemType[]
            ).includes(type),
          },
          { "pe-0": readonly || type === "look" },
        )}
      >
        <div
          className={classNames("product-info", {
            "me-0": (["mini", "look"] as TShoppingBagItemType[]).includes(type),
          })}
        >
          <ProductTitle
            variant="PLP"
            theme={theme}
            size={type === "look" || hasSmallTitle ? "small" : undefined}
            brandName={brandName}
            brandLink={brandLink ?? null}
            productName={productName}
            productLink={productLink ?? null}
          />
          <ProductConfig
            showSize={showSize}
            color={color}
            size={size}
            labels={labels}
          />
          {!["mini", "look"].includes(type) && (
            <ShopingBagProductActions
              isRemoveAvailable={isRemoveAvailable}
              isFavoriteAvailable={isFavoriteAvailable}
              labels={labels}
              onItemRemove={removeItem}
              onFavouriteAdd={onFavourite}
            />
          )}
        </div>
        <ShopingBagQuantity
          type={type}
          hasDisableQuantity={hasDisableQuantity}
          readonly={readonly}
          variant={variant}
          selectedQty={selectedQty}
          labels={labels}
          opened={opened}
          disableQty={disableQty}
          availableQuantity={availableQuantity}
          handleQuantityChange={handleQtyChange}
        />
        <ShoppingBagAdditionInfo
          sportLoversPrice={sportLoversPrice}
          type={type}
          prices={prices}
          readonly={readonly}
          isRemoveAvailable={isRemoveAvailable}
          isFavoriteAvailable={isFavoriteAvailable}
          onRemoveItem={removeItem}
          onFavourite={onFavourite}
          handleAddToCart={handleAddToCart}
          disabled={disabled}
          isAddedToCart={isAddedToCart}
        />
      </figcaption>
    </figure>
  );
};

export default ShoppingBagItem;
